import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/** Accessible modal: focus trap entry, Escape to close, backdrop click to close. */
export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal__head">{title}</div>
        <div className="modal__body">{children}</div>
        <div className="modal__foot">
          {footer ?? <button type="button" className="btn" onClick={onClose}>닫기</button>}
        </div>
      </div>
    </div>
  );
}

/**
 * Confirmation for a destructive or wide-reaching action.
 * A reason is mandatory, mirroring the server's change_reason requirement.
 */
export function ConfirmModal({
  title,
  description,
  confirmLabel,
  requireReason = true,
  onConfirm,
  onClose,
  busy,
}: {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  requireReason?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>취소</button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy}
            onClick={() => {
              const reason = reasonRef.current?.value.trim() ?? '';
              if (requireReason && reason.length < 2) {
                reasonRef.current?.focus();
                return;
              }
              onConfirm(reason);
            }}
          >
            {busy ? '처리 중…' : confirmLabel}
          </button>
        </>
      }
    >
      <div style={{ marginBottom: 16 }}>{description}</div>
      {requireReason && (
        <div className="field">
          <label htmlFor="confirm-reason">변경 사유 (감사 기록)</label>
          <textarea id="confirm-reason" ref={reasonRef} rows={3} required />
          <div className="field__hint">사유는 운영 기록에 저장 · 이후 조회 가능</div>
        </div>
      )}
    </Modal>
  );
}
